import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Notification } from '@evogard/contract';
import { api, ok } from './api.js';
import { useSessionContext } from './session.js';

export const NOTIFICATIONS_KEY = ['plataforma', 'notificacoes'] as const;

/**
 * As últimas notificações da própria sessão — o histórico do sino.
 *
 * Escopado por tenant na CHAVE, como toda consulta da casa: trocar de base
 * com o menu aberto na memória não pode mostrar o aviso da base anterior.
 * `useSwitchTenant` já invalida TUDO ao trocar (`session.ts`), então esta
 * consulta refaz sozinha — não precisa de tratamento especial aqui.
 */
export function useMyNotifications() {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;

  return useQuery({
    queryKey: [...NOTIFICATIONS_KEY, tenantId],
    enabled: Boolean(context),
    queryFn: async (): Promise<Notification[]> => {
      const r = await api.platform.listMyNotifications({ query: {} });
      return ok(r, 200, 'Não foi possível carregar as notificações.');
    },
    // o WebSocket invalida ao vivo (`realtime.ts`); aqui é só o piso de quem
    // abre o menu sem ter recebido nada em tempo real ainda
    staleTime: 30_000,
  });
}

/**
 * As entidades com aviso NÃO LIDO — o insumo do ponto laranja no rail.
 *
 * ## Por que devolve um conjunto e não um número
 *
 * O rail pergunta "este item tem novidade?", não "quantas". O ponto é de 6px:
 * não cabe número, e pôr um obrigaria a escolher entre a contagem do módulo e a
 * do item. O sino já responde "quantas", e responde uma vez para a pessoa
 * inteira — que é a pergunta certa para ele.
 *
 * ## Por que reusa a consulta do sino em vez de ter a própria
 *
 * Mesma `queryKey`, mesmo cache, e o tempo real já a invalida
 * (`lib/realtime.tsx`). Uma consulta própria seria uma segunda ida ao servidor
 * por requisição autenticada, e — pior — as duas poderiam discordar por alguns
 * segundos: o sino aceso e o rail apagado, sobre o mesmo aviso.
 *
 * O limite da rota (20 por padrão) é o teto do que se enxerga aqui. Isso não
 * distorce a resposta: com vinte avisos não lidos de uma entidade só, a pergunta
 * "tem novidade?" continua sendo sim.
 */
export function useUnreadEntities(): Set<string> {
  const { data } = useMyNotifications();

  return new Set((data ?? []).filter((n) => n.readAt === null).map((n) => n.entity));
}

/**
 * Para onde o clique num aviso leva — ou `null`, e a linha continua sendo só
 * texto.
 *
 * **Parcial de propósito**, como `notificationEntity` em `menu.ts`: um aviso
 * só vira link quando a tela de destino existe e a pessoa que o recebeu é
 * quem pode abri-la. O do pagamento da premiação é sempre do PRÓPRIO
 * consultor (`notifyConsultant`), e o destino é o quadro dele com o cartão
 * aberto — que a rota exige `management.payments:ver_meus`. Sem essa ação, o
 * link cairia numa tela que responde 403, e a linha fica texto.
 */
export function notificationLink(
  n: Pick<Notification, 'entity' | 'entityId' | 'type'>,
  can: (resource: string, action: string) => boolean,
): { to: string; search: Record<string, number> } | null {
  /* O lote que acabou de fechar ainda espera a PRIMEIRA nota, e ela é
     enviada no Início do Comercial, no cartão do lote (desde 28/10/2026) — o
     quadro do consultor não tem essa fase. */
  if (n.entity === 'management_payments' && n.type === 'lote_fechado') {
    return can('sales.management', 'ver') ? { to: '/sales/home', search: {} } : null;
  }
  if (n.entity === 'management_payments' && can('management.payments', 'ver_meus')) {
    const card = Number(n.entityId);
    if (Number.isInteger(card) && card > 0) {
      return { to: '/sales/recurrence-payments/mine', search: { card } };
    }
  }
  /* A despesa de venda devolvida ou paga abre no quadro de quem a lançou. */
  if (n.entity === 'sales_expenses' && can('sales.expenses', 'ver_minhas')) {
    const card = Number(n.entityId);
    if (Number.isInteger(card) && card > 0) {
      return { to: '/sales/expenses/mine', search: { card } };
    }
  }
  return null;
}

/**
 * Marca como lidas — chamado quando o sino ABRE, nunca ao listar (ver o
 * comentário da rota, em `packages/contract/src/platform.ts`).
 */
export function useMarkNotificationsRead() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const r = await api.platform.markNotificationsRead({ body: undefined });
      if (r.status !== 204) throw new Error('Não foi possível marcar como lida.');
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: NOTIFICATIONS_KEY }),
  });
}
